/**
 * Guardián de rutas: decide qué se ve según el estado de sesión.
 *
 * DÓNDE SE APLICA Y DÓNDE NO. Solo envuelve `/registro` y `/mi-cuenta`.
 * `/espacios` y `/disponibilidad` son de solo lectura y siguen abiertas: pedir
 * sesión para mirar un catálogo no protege nada y hoy, sin OAuth configurado en
 * el despliegue, dejaría la aplicación entera inaccesible.
 *
 * POR QUÉ RECIBE `permite` EN VEZ DE EXIGIR SIEMPRE `registrado`. `/registro`
 * es la única pantalla que tiene sentido justo cuando la persona NO está
 * registrada. Si el guardián mandara todo `sin_registrar` a `/registro` sin
 * excepción, esa ruta se redirigiría a sí misma para siempre. Con la lista de
 * estados permitidos, `/registro` declara que acepta los dos y decide por su
 * cuenta qué enseñar en cada uno.
 *
 * El contenido llega como función y no como `children` sueltos para que reciba
 * el perfil YA ESTRECHADO: `/mi-cuenta` puede leer `perfil.usuario` sin volver
 * a comprobar el estado, y el compilador impide leer un campo del estado
 * equivocado.
 */

import type { ReactNode } from 'react'
import { Link, Navigate } from '@tanstack/react-router'
import { Estado } from '../componentes/Estado'
import { textoDeError } from '../api/errores'
import type { EstadoConSesion, Perfil } from '../api/perfil'
import { usePerfil } from './usePerfil'
import { BotonEntrar } from './BotonEntrar'

interface PropiedadesProtegida<E extends EstadoConSesion> {
  /** Estados de sesión que pueden ver el contenido. */
  permite: readonly E[]
  children: (perfil: Extract<Perfil, { estado: E }>) => ReactNode
}

export function Protegida<E extends EstadoConSesion>({
  permite,
  children,
}: PropiedadesProtegida<E>) {
  const consulta = usePerfil()

  if (consulta.isPending) {
    return (
      <Estado
        tono="neutro"
        titulo="Comprobando tu sesión…"
        detalle="Petición a GET /api/perfil."
        enVivo
      />
    )
  }

  if (consulta.isError) {
    return (
      <Estado
        tono="error"
        titulo="No se pudo comprobar tu sesión."
        detalle={textoDeError(consulta.error)}
        enVivo
      >
        <button
          type="button"
          className="boton"
          onClick={() => void consulta.refetch()}
          disabled={consulta.isFetching}
        >
          {consulta.isFetching ? 'Reintentando…' : 'Reintentar'}
        </button>
      </Estado>
    )
  }

  const perfil = consulta.data

  if (perfil.estado === 'anonimo') {
    // Sin OAuth configurado en el servidor NO se ofrece el botón: llevaría a un
    // 503. Es el estado del despliegue de hoy, y se dice con todas sus letras
    // en vez de dejar una pantalla que parece rota.
    if (!perfil.autenticacion_disponible) {
      return (
        <Estado
          tono="aviso"
          titulo="El inicio de sesión todavía no está configurado en este servidor."
          detalle="Esta pantalla necesita una sesión, y aquí no hay con qué crearla. Las secciones de consulta —espacios y disponibilidad— sí se pueden ver sin entrar."
          enVivo
        >
          <Link to="/" className="boton">
            Ir al inicio
          </Link>
        </Estado>
      )
    }

    return (
      <Estado
        tono="aviso"
        titulo="Hace falta iniciar sesión para ver esta página."
        detalle="Entra con tu cuenta institucional de Google."
        enVivo
      >
        <BotonEntrar />
      </Estado>
    )
  }

  if (esEstadoPermitido(perfil, permite)) {
    return <>{children(perfil)}</>
  }

  // Queda un solo caso: hay sesión de Google pero falta el registro, y esta
  // pantalla no lo acepta. El sitio donde eso se resuelve es `/registro`.
  if (perfil.estado === 'sin_registrar') {
    return <Navigate to="/registro" replace />
  }

  // Y aquí, alguien registrado en una pantalla que solo admite `sin_registrar`.
  // Hoy no hay ninguna así —`/registro` admite los dos estados—, pero el caso
  // se cierra con un mensaje en vez de con una pantalla en blanco.
  return (
    <Estado
      tono="aviso"
      titulo="Esta página no corresponde a tu estado de sesión."
      detalle="Tu cuenta ya está registrada en el sistema."
      enVivo
    >
      <Link to="/mi-cuenta" className="boton">
        Ver mi cuenta
      </Link>
    </Estado>
  )
}

/**
 * `perfil.estado` está en la lista. Se escribe con `some` y `===` en vez de con
 * `includes` para que TypeScript compare `E` contra el estado sin ninguna
 * conversión de tipo de por medio.
 */
function esEstadoPermitido<E extends EstadoConSesion>(
  perfil: Perfil,
  permite: readonly E[],
): perfil is Extract<Perfil, { estado: E }> {
  return permite.some((permitido) => permitido === perfil.estado)
}
